import { Fragment, FunctionComponent, JSX, useEffect, useState } from 'react'
import { Daycare, DaycareType } from '@/types/Daycare'
import { Role } from '@/types/Role'
import styles from './eventsList.module.scss'
import { EventDay } from '@/types/EventDay'
import { ClosingDay } from '@/types/ClosingDays'
import Title from '@/components/_custom/Title/title'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import AddEventsClosingDaysDialog from './Dialog/addEventsClosingDays'
import { useLazyQuery } from '@apollo/client'
import { GET_EVENT_DAY } from '@/modules/API/daycare/eventDayHandler'
import moment from 'moment-timezone'
import { GET_CLOSING_DAY } from '@/modules/API/daycare/closingDayHandler'
import Loading from '@/components/Loading/Loading'
import EventDayRow from './eventDay'
import ClosingDayRow from './closingDay'
import { ArrowForward } from '@mui/icons-material'
import { useAuth } from '@/modules/context/AuthContext'
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router-dom'
import { TaskNote } from '@/types/TaskNote.ts'
import { GET_TASK_NOTES_BY_DAYCARE } from '@/modules/API/taskNote/getTaskNotes.ts'
import TaskNoteComponent from '../taskNote/taskNoteComponent/taskNoteComponent.tsx'

interface EventsListProps {
    role: Role
    daycare: Daycare
    fromDate: string
    toDate: string
}

const EventsList: FunctionComponent<EventsListProps> = ({ role, daycare, fromDate, toDate }: EventsListProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const location: Location = useLocation()
    const navigate: NavigateFunction = useNavigate()

    const [eventsList, setEventsList] = useState<Array<EventDay>>([])
    const [closingDaysList, setClosingDaysList] = useState<Array<ClosingDay>>([])
    const [taskNoteList, setTaskNoteList] = useState<Array<TaskNote>>([])

    const [bookedEventsList, setBookedEventsList] = useState<Array<EventDay>>([])
    const [eventsAndClosingDaysList, setEventsAndClosingDaysList] = useState<Array<EventDay | ClosingDay | TaskNote>>(
        []
    )

    const [oneEventOrClosingDay, setOneEventOrClosingDay] = useState<boolean>(false)

    const [isAddEventDayDialogOpen, setIsAddEventDayDialogOpen] = useState<boolean>(false)

    const isPastRange: boolean =
        moment(fromDate, 'DD-MM-YYYY').isBefore(moment(), 'day') &&
        moment(toDate, 'DD-MM-YYYY').isBefore(moment(), 'day')

    const [getEventDays, { loading: loadingEvents, data: dataEvents, refetch: refetchEventDays }] = useLazyQuery(
        GET_EVENT_DAY,
        {
            fetchPolicy: 'network-only',
            variables: {
                daycare_id: daycare.id,
                from: fromDate,
                to: toDate
            }
        }
    )

    useEffect(() => {
        if (dataEvents && dataEvents.getEventDay && dataEvents.getEventDay.length > 0) {
            setEventsList(dataEvents.getEventDay)
        }
    }, [dataEvents])

    const [getClosingDays, { loading: loadingClosingDay, data: dataClosingDay, refetch: refetchClosingDays }] =
        useLazyQuery(GET_CLOSING_DAY, {
            fetchPolicy: 'network-only',
            variables: {
                daycare_id: daycare?.id,
                from: fromDate,
                to: toDate
            }
        })

    useEffect(() => {
        if (dataClosingDay && dataClosingDay.getClosingDay && dataClosingDay.getClosingDay.length > 0) {
            setClosingDaysList(dataClosingDay.getClosingDay)
        }
    }, [dataClosingDay])

    const [getTaskNotes, { loading: loadingTaskNote, data: dataTaskNote, refetch: refetchTaskNote }] = useLazyQuery(
        GET_TASK_NOTES_BY_DAYCARE,
        {
            fetchPolicy: 'network-only',
            variables: {
                daycareId: daycare?.id,
                from: fromDate,
                to: toDate
            }
        }
    )

    useEffect(() => {
        if (dataTaskNote && dataTaskNote.getTaskNotesByDaycare && dataTaskNote.getTaskNotesByDaycare.length > 0) {
            setTaskNoteList(dataTaskNote.getTaskNotesByDaycare)
        }
    }, [dataTaskNote])

    async function refetchAllEvents(): Promise<void> {
        const { data } = await refetchEventDays({
            variables: {
                daycare_id: daycare.id,
                from: fromDate,
                to: toDate
            }
        })

        if (data.getEventDay && data.getEventDay.length > 0) {
            setEventsList(data.getEventDay)
        } else {
            setEventsList([])
        }

        const { data: dataClosingDay } = await refetchClosingDays({
            variables: {
                daycare_id: daycare.id,
                from: fromDate,
                to: toDate
            }
        })

        if (dataClosingDay.getClosingDay && dataClosingDay.getClosingDay.length > 0) {
            setClosingDaysList(dataClosingDay.getClosingDay)
        } else {
            setClosingDaysList([])
        }

        if (user?.role === Role.parent) {
            const { data: dataTaskNote } = await refetchTaskNote({
                variables: {
                    daycareId: daycare?.id,
                    from: fromDate,
                    to: toDate
                }
            })

            if (dataTaskNote.getTaskNotesByDaycare && dataTaskNote.getTaskNotesByDaycare.length > 0) {
                setTaskNoteList(dataTaskNote.getTaskNotesByDaycare)
            } else {
                setTaskNoteList([])
            }
        }
    }
    function handleBackToEvents(): void {
        setEventsAndClosingDaysList([])
        setBookedEventsList([])

        const bookedEventsList: EventDay[] = eventsList.filter((eventBooked: EventDay) => eventBooked.booked)

        const unBookedEventsList: EventDay[] = eventsList.filter((eventUnBooked: EventDay) => !eventUnBooked.booked)

        setBookedEventsList(bookedEventsList)
        setEventsAndClosingDaysList([...unBookedEventsList, ...closingDaysList, ...taskNoteList])
        setOneEventOrClosingDay(false)

        navigate({
            pathname: '/events',
            search: 'toDate=' + moment().add(1, 'years').format('DD-MM-YYYY')
        })
    }

    useEffect(() => {
        setEventsList([])
        setClosingDaysList([])
        setTaskNoteList([])

        async function fetchData(): Promise<void> {
            await getEventDays()
            await getClosingDays()
            if (user?.role === Role.parent) await getTaskNotes()
        }

        fetchData()
    }, [location.search, user?.currentDaycare])

    useEffect(() => {
        setOneEventOrClosingDay(false)
        setEventsAndClosingDaysList([])
        setBookedEventsList([])

        const params: URLSearchParams = new URLSearchParams(location.search)

        if (params.has('event')) {
            const idNum: number = Number(params.get('event'))
            if (!Number.isNaN(idNum)) {
                const eventFound: EventDay | undefined = eventsList.find(
                    (event: EventDay): boolean => event.id === idNum
                )
                if (eventFound) {
                    setEventsAndClosingDaysList([eventFound])
                    setOneEventOrClosingDay(true)
                    return
                }
            }
            setEventsAndClosingDaysList([...eventsList, ...closingDaysList, ...taskNoteList])
            return
        }

        if (params.has('closingDay')) {
            const idNum: number = Number(params.get('closingDay'))
            if (!Number.isNaN(idNum)) {
                const closingDayFound: ClosingDay | undefined = closingDaysList.find(
                    (closingDay: ClosingDay): boolean => closingDay.id === idNum
                )
                if (closingDayFound) {
                    setEventsAndClosingDaysList([closingDayFound])
                    setOneEventOrClosingDay(true)
                    return
                }
            }
            setEventsAndClosingDaysList([...eventsList, ...closingDaysList, ...taskNoteList])
            return
        }

        if (params.has('taskNote')) {
            const idNum: number = Number(params.get('taskNote'))
            if (!Number.isNaN(idNum)) {
                const taskNoteFound: TaskNote | undefined = taskNoteList.find(
                    (taskNote: TaskNote): boolean => taskNote.id === idNum
                )
                if (taskNoteFound) {
                    setEventsAndClosingDaysList([taskNoteFound])
                    setOneEventOrClosingDay(true)
                    return
                }
            }
            setEventsAndClosingDaysList([...eventsList, ...closingDaysList, ...taskNoteList])
            return
        }

        const booked: EventDay[] = eventsList.filter((e: EventDay): boolean => e.booked)
        const unBooked: EventDay[] = eventsList.filter((e: EventDay): boolean => !e.booked)
        setBookedEventsList(booked)
        setEventsAndClosingDaysList([...unBooked, ...closingDaysList, ...taskNoteList])
    }, [eventsList, closingDaysList, taskNoteList, location.search])

    return (
        <>
            {bookedEventsList.length > 0 && (
                <div className={styles.EventsListsPlacement}>
                    <div className={styles.Form}>
                        <div className={styles.TitleAndButton}>
                            <Title text={t('bookedEvents')} className={styles.Title} />
                            {(user?.currentDaycareRights.canWriteEvents ||
                                user?.currentDaycareRights.canWriteClosingDays) &&
                                role !== Role.parent &&
                                !oneEventOrClosingDay && (
                                    <ButtonTextIcon type={'add'} onClick={() => setIsAddEventDayDialogOpen(true)} />
                                )}
                        </div>
                        {(loadingEvents || loadingClosingDay || loadingTaskNote) && <Loading overall={false} />}
                        <div className={styles.Container}>
                            {bookedEventsList
                                .sort((a: EventDay, b: EventDay) => {
                                    const result: number =
                                        moment(a.date, 'DD-MM-YYYY').toDate() > moment(b.date, 'DD-MM-YYYY').toDate()
                                            ? 1
                                            : -1
                                    return isPastRange ? -result : result
                                })
                                .map((event: EventDay) => (
                                    <div
                                        className={styles.Card}
                                        key={`${event.id.toString()}${event.booked ? '_booked' : '_unbooked'}`}
                                    >
                                        <EventDayRow
                                            event={event}
                                            daycare={daycare}
                                            isParent={role === Role.parent}
                                            canUpdate={user?.currentDaycareRights.canWriteEvents || false}
                                            refetchAllEvents={refetchAllEvents}
                                        />
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            )}
            <div className={styles.EventsListsPlacement}>
                <div className={styles.Form}>
                    <div className={styles.TitleAndButton}>
                        <Title text={t('events')} className={styles.Title} />
                        {(user?.currentDaycareRights.canWriteEvents ||
                            user?.currentDaycareRights.canWriteClosingDays) &&
                            role !== Role.parent &&
                            !oneEventOrClosingDay && (
                                <ButtonTextIcon type={'add'} onClick={() => setIsAddEventDayDialogOpen(true)} />
                            )}
                    </div>
                    {(loadingEvents || loadingClosingDay || loadingTaskNote) && <Loading overall={false} />}
                    <div className={styles.Container}>
                        {eventsAndClosingDaysList.length === 0 && bookedEventsList.length === 0 ? (
                            <>
                                <p className={styles.NoEventsText}>{t('noEvents')}</p>
                                <div className={styles.ButtonContainer}>
                                    <ButtonTextIcon
                                        type={'return'}
                                        className={styles.ShowMoreButton}
                                        onClick={() => navigate('/news')}
                                        parent={role === Role.parent}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                {eventsAndClosingDaysList
                                    .sort(
                                        (
                                            a: EventDay | ClosingDay | TaskNote,
                                            b: EventDay | ClosingDay | TaskNote
                                        ): number => {
                                            let result: number = 0
                                            if (isClosingDay(a) && isEventDay(b)) {
                                                result =
                                                    moment(a.startDate, 'DD-MM-YYYY').toDate() >
                                                    moment(b.date, 'DD-MM-YYYY').toDate()
                                                        ? 1
                                                        : -1
                                            } else if (isEventDay(a) && isClosingDay(b)) {
                                                result =
                                                    moment(a.date, 'DD-MM-YYYY').toDate() >
                                                    moment(b.startDate, 'DD-MM-YYYY').toDate()
                                                        ? 1
                                                        : -1
                                            } else if (isEventDay(a) && isEventDay(b)) {
                                                result =
                                                    moment(a.date, 'DD-MM-YYYY').toDate() >
                                                    moment(b.date, 'DD-MM-YYYY').toDate()
                                                        ? 1
                                                        : -1
                                            } else if (isClosingDay(a) && isClosingDay(b)) {
                                                result =
                                                    moment(a.startDate, 'DD-MM-YYYY').toDate() >
                                                    moment(b.startDate, 'DD-MM-YYYY').toDate()
                                                        ? 1
                                                        : -1
                                            }
                                            return isPastRange ? -result : result
                                        }
                                    )
                                    .map(
                                        (item: EventDay | ClosingDay | TaskNote, index: number): JSX.Element => (
                                            <Fragment key={createIndexForItem(item, index)}>
                                                {isEventDay(item) ? (
                                                    <div className={styles.Card}>
                                                        <EventDayRow
                                                            event={item}
                                                            canUpdate={
                                                                user?.currentDaycareRights.canWriteEvents || false
                                                            }
                                                            isParent={role === Role.parent}
                                                            refetchAllEvents={refetchAllEvents}
                                                            daycare={daycare}
                                                        />
                                                    </div>
                                                ) : isClosingDay(item) ? (
                                                    <div className={styles.Card}>
                                                        <ClosingDayRow
                                                            closingDay={item}
                                                            canUpdate={
                                                                user?.currentDaycareRights.canWriteClosingDays || false
                                                            }
                                                            parent={role === Role.parent}
                                                            refetchAllEvents={refetchAllEvents}
                                                            daycare={daycare}
                                                            closingDays={closingDaysList}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className={styles.Card}>
                                                        <TaskNoteComponent
                                                            taskNote={item}
                                                            isParent={role === Role.parent}
                                                            isRpeOrLaep={
                                                                daycare.type === DaycareType.rpe ||
                                                                daycare.type === DaycareType.laep
                                                            }
                                                            canUpdate={
                                                                user?.currentDaycareRights.canWriteTaskNote || false
                                                            }
                                                            daycareId={daycare.id}
                                                            handleRefetch={refetchAllEvents}
                                                        />
                                                    </div>
                                                )}
                                            </Fragment>
                                        )
                                    )}
                                <div className={styles.ButtonContainer}>
                                    {oneEventOrClosingDay ? (
                                        <ButtonTextIcon
                                            type={'custom'}
                                            className={styles.ShowMoreButton}
                                            customText={t('showOtherEvents')}
                                            customIcon={<ArrowForward />}
                                            onClick={handleBackToEvents}
                                            parent={role === Role.parent}
                                        />
                                    ) : (
                                        <ButtonTextIcon
                                            type={'return'}
                                            className={styles.ShowMoreButton}
                                            onClick={() => navigate('/news')}
                                            parent={role === Role.parent}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <AddEventsClosingDaysDialog
                    open={isAddEventDayDialogOpen}
                    handleClose={() => setIsAddEventDayDialogOpen(false)}
                    daycareId={daycare.id}
                    refetchAllEvents={refetchAllEvents}
                    closingDays={closingDaysList}
                />
            </div>
        </>
    )
}

export default EventsList

function isClosingDay(item: ClosingDay | TaskNote | EventDay): item is ClosingDay {
    return 'startDate' in item && 'endDate' in item && 'closingDaysReason' in item
}

function isEventDay(item: ClosingDay | TaskNote | EventDay): item is EventDay {
    return 'date' in item && 'startHour' in item && 'endHour' in item && 'reservation' in item
}

function createIndexForItem(item: EventDay | ClosingDay | TaskNote, index: number): string {
    const eventDay: boolean = isEventDay(item)
    return `${item.id.toString()}_${index.toString()}${eventDay ? ((item as EventDay).booked ? '_booked' : '_unbooked') : '_other'}`
}
